/**
 * Tiktok 官方尺码表（bg.goods.sizecharts.*）工具。
 * 必填判定：sizecharts.class.get 有 sizeSpecClassCat.classId 即为必填。
 */

import { fixedUi } from '@/i18n/fixedUi';
import {
  formatTiktokSizeChartMeasureLabel,
  getCachedTiktokSizeChartMeasureLabel,
} from '@/tiktok-platform/utils/tiktokSizeChartMeasurementLabels';

/** 平台固定的「尺码」列头 ID（与站内尺码库 buildSizeChartDataFromDraft 一致） */
export const SIZE_HEADER_ID = 'size_chart_size_system_size';

/**
 * TikTok size_chart_id / platformBusinessId 常超过 Number.MAX_SAFE_INTEGER，
 * 禁止 Number()，一律按十进制数字串处理。
 */
export function tiktokSizeChartId(raw) {
  const s = String(raw ?? '').trim();
  if (!s || s === '0' || s === 'null' || s === 'undefined') return '';
  // 平台 ID 为十进制数字串；拒绝 'x' 等脏值
  if (!/^\d+$/.test(s)) return '';
  return s;
}

export function isTiktokSizeChartId(raw) {
  return Boolean(tiktokSizeChartId(raw));
}

export function asSizeChartList(v) {
  if (Array.isArray(v)) return v;
  if (!v || typeof v !== 'object') return [];
  if (Array.isArray(v.items)) return v.items;
  if (Array.isArray(v.content)) return v.content;
  if (Array.isArray(v.sizeSpecDataList)) return v.sizeSpecDataList;
  if (Array.isArray(v.list)) return v.list;
  return [];
}

export function parseSizeChartClass(res) {
  const cls = res?.sizeSpecClassCat || res?.result?.sizeSpecClassCat || null;
  if (!cls || !(Number(cls.classId) > 0)) {
    return { required: false, sizeClass: null };
  }
  let required = true;
  if (typeof cls.isRequired === 'boolean') required = cls.isRequired;
  else if (typeof cls.is_required === 'boolean') required = cls.is_required;
  return { required, sizeClass: cls };
}

export function pickSizeMetaLists(metaRes, settingsRes) {
  const groupList = metaRes?.sizeSpecMeta?.groupList
    || settingsRes?.mappingContent?.meta?.groupList
    || [{ id: 1, name: '尺码' }];
  const elementList = metaRes?.sizeSpecMeta?.elementList
    || settingsRes?.mappingContent?.meta?.elementList
    || [];
  return { groupList, elementList };
}

/**
 * 从草稿行取出尺码名。编辑回填时 meta.groupList[0].id 可能与草稿不一致，
 * 不能只认当前 groupId，否则整表测量值会被当成空行丢掉。
 */
export function extractSizeLabelFromChartRecord(values, {
  groupId = '1',
  elementIds = [],
} = {}) {
  if (!values || typeof values !== 'object') return '';
  const preferred = String(values[groupId] ?? values[String(groupId)] ?? '').trim();
  if (preferred) return preferred;
  for (const key of ['1', 1, 'sizeName', 'name']) {
    const hit = String(values[key] ?? '').trim();
    if (hit) return hit;
  }
  const skip = new Set([
    String(groupId),
    '1',
    'sizeName',
    'groupId',
    'name',
    ...(Array.isArray(elementIds) ? elementIds.map(String) : []),
  ]);
  for (const [key, raw] of Object.entries(values)) {
    if (skip.has(String(key))) continue;
    const text = String(raw ?? '').trim();
    if (text) return text;
  }
  return '';
}

export function buildSizeChartRecords({
  groupList = [],
  elementList = [],
  sizeNames = [],
  prevRecords = [],
} = {}) {
  const groupId = String(groupList[0]?.id ?? 1);
  const elementIds = (Array.isArray(elementList) ? elementList : [])
    .map((el) => String(el?.id ?? ''))
    .filter(Boolean);
  const elementIdSet = new Set(elementIds);
  const names = (Array.isArray(sizeNames) ? sizeNames : [])
    .map((n) => String(n || '').trim())
    .filter(Boolean);
  // 行数严格跟随主规格已选尺码，不自动补 S/M/L
  const prevBySize = new Map();
  (Array.isArray(prevRecords) ? prevRecords : []).forEach((row) => {
    const srcValues = row?.values && typeof row.values === 'object' ? row.values : {};
    const key = extractSizeLabelFromChartRecord(srcValues, { groupId, elementIds });
    if (!key) return;
    prevBySize.set(key, srcValues);
    prevBySize.set(key.toLowerCase(), srcValues);
  });
  return names.map((sizeName) => {
    const prev = prevBySize.get(String(sizeName))
      || prevBySize.get(String(sizeName).toLowerCase())
      || {};
    const values = { [groupId]: String(sizeName) };
    (elementList || []).forEach((el) => {
      const id = String(el.id);
      let raw = prev[id] != null ? prev[id] : prev[el.id];
      // 草稿 element id 漂移时：按「非尺码列」的同名测量值兜底（少见）
      if ((raw == null || String(raw).trim() === '') && el?.name) {
        const nameKey = String(el.name).trim().toLowerCase();
        for (const [k, v] of Object.entries(prev)) {
          if (elementIdSet.has(String(k)) || String(k) === groupId) continue;
          if (String(k).trim().toLowerCase() === nameKey) {
            raw = v;
            break;
          }
        }
      }
      values[id] = raw != null ? String(raw) : '';
    });
    // meta.elementList 尚未回来时不要丢掉草稿测量值，否则会经 onDraftChange 把空表写进产品模板
    Object.entries(prev).forEach(([key, raw]) => {
      const k = String(key);
      if (k === groupId || k === '1' || k === 'sizeName' || k === 'groupId' || k === 'name') return;
      if (Object.prototype.hasOwnProperty.call(values, k) && String(values[k] ?? '').trim()) return;
      if (raw == null || String(raw).trim() === '') return;
      values[k] = String(raw);
    });
    return { values };
  });
}

/**
 * 平台 size_chart_data.table（headers/rows/cells）→ 站内 officialSizeChart.records。
 */
export function recordsFromTiktokSizeChartData(sizeChartData, {
  groupList = [],
  elementList = [],
} = {}) {
  const data = sizeChartData && typeof sizeChartData === 'object' ? sizeChartData : null;
  const table = data?.table && typeof data.table === 'object' ? data.table : null;
  if (!table) return null;
  const headers = Array.isArray(table.headers) ? table.headers : [];
  const rows = Array.isArray(table.rows) ? table.rows : [];
  if (!headers.length || !rows.length) return null;

  const sizeHeaderIdx = headers.findIndex((h) => String(h?.id || '') === SIZE_HEADER_ID);
  const sizeIdx = sizeHeaderIdx >= 0 ? sizeHeaderIdx : 0;
  const measureHeaders = headers
    .map((h, idx) => ({ h, idx }))
    .filter(({ idx }) => idx !== sizeIdx);

  const dstGroupId = String(groupList[0]?.id ?? 1);
  const elById = new Map((elementList || []).map((el) => [String(el.id), el]));
  const elByName = new Map();
  (elementList || []).forEach((el) => {
    const key = String(el?.name || '').trim().toLowerCase();
    if (key) elByName.set(key, el);
  });

  let filled = 0;
  const records = rows.map((row) => {
    const cells = Array.isArray(row?.cells) ? row.cells : [];
    const sizeName = String(cells[sizeIdx]?.value ?? '').trim();
    const values = { [dstGroupId]: sizeName };
    if (sizeName) filled += 1;

    if (elementList?.length) {
      (elementList || []).forEach((el) => {
        const dstId = String(el.id);
        const byId = measureHeaders.find(({ h }) => String(h?.id || '') === dstId);
        const byName = !byId
          ? measureHeaders.find(({ h }) => (
            String(h?.name || '').trim().toLowerCase() === String(el?.name || '').trim().toLowerCase()
          ))
          : null;
        const hit = byId || byName;
        const raw = hit ? cells[hit.idx]?.value : '';
        values[dstId] = raw == null ? '' : String(raw);
        if (String(values[dstId] || '').trim()) filled += 1;
      });
    } else {
      measureHeaders.forEach(({ h, idx }) => {
        const id = String(h?.id || '').trim();
        if (!id) return;
        values[id] = cells[idx]?.value == null ? '' : String(cells[idx].value);
        if (String(values[id] || '').trim()) filled += 1;
        if (!elById.has(id) && h?.name) {
          elByName.set(String(h.name).trim().toLowerCase(), { id, name: h.name });
        }
      });
    }
    return { values };
  }).filter((row) => String(row.values?.[dstGroupId] || '').trim());

  return records.length ? { records, filled } : null;
}

/** records 是否含任意测量值（排除尺码名 / 分组壳字段） */
export function officialSizeChartRecordsHaveValues(records, groupList = []) {
  const groupId = String(groupList[0]?.id ?? 1);
  const skip = new Set([groupId, 'sizeName', 'groupId', 'name']);
  return (Array.isArray(records) ? records : []).some((row) => {
    const values = row?.values && typeof row.values === 'object' ? row.values : row;
    if (!values || typeof values !== 'object') return false;
    return Object.keys(values).some((key) => {
      if (skip.has(String(key))) return false;
      const val = values[key];
      return val != null && String(val).trim() !== '';
    });
  });
}

/** 草稿是否值得回填（有测量值或已选库表） */
export function officialSizeChartDraftHasContent(draft, groupList = []) {
  if (!draft || typeof draft !== 'object') return false;
  if (Array.isArray(draft.selectedBusinessIds) && draft.selectedBusinessIds.some((id) => {
    return Boolean(tiktokSizeChartId(id)) || Number(id) > 0;
  })) {
    return true;
  }
  if (String(draft.selectedLibraryId || '').trim()) return true;
  if (draft.sizeChartData && typeof draft.sizeChartData === 'object') return true;
  return officialSizeChartRecordsHaveValues(draft.records, groupList);
}

/**
 * 编辑回填：优先 records；若只有 sizeChartData（尺码库结构化表）则转成 records。
 */
export function resolveOfficialSizeChartSeedRecords(draft, groupList = [], elementList = []) {
  const seeded = Array.isArray(draft?.records) ? draft.records : [];
  if (officialSizeChartRecordsHaveValues(seeded, groupList)) return seeded;
  const sizeChartData = draft?.sizeChartData
    || draft?.size_chart_data
    || (Array.isArray(draft?.sizeChartDatas)
      ? (draft.sizeChartDatas[0]?.size_chart_data || draft.sizeChartDatas[0]?.sizeChartData)
      : null);
  if (!sizeChartData || typeof sizeChartData !== 'object') return seeded;
  const converted = recordsFromTiktokSizeChartData(sizeChartData, { groupList, elementList });
  return converted?.records?.length ? converted.records : seeded;
}

/**
 * 官方尺码表草稿内容签名：用于避免 onDraftChange 每次新对象引用触发父级 setForm 死循环。
 */
export function officialSizeChartDraftSignature(draft) {
  if (!draft || typeof draft !== 'object') return '';
  try {
    return JSON.stringify({
      mode: draft.mode || '',
      classId: draft.sizeClass?.classId ?? '',
      classType: draft.sizeClass?.classType ?? '',
      selectedBusinessIds: Array.isArray(draft.selectedBusinessIds)
        ? draft.selectedBusinessIds.map((id) => tiktokSizeChartId(id) || String(id || '')).filter(Boolean)
        : [],
      selectedLibraryId: String(draft.selectedLibraryId || '').trim(),
      records: Array.isArray(draft.records) ? draft.records : [],
      sizeChartData: draft.sizeChartData && typeof draft.sizeChartData === 'object'
        ? draft.sizeChartData
        : null,
      groupIds: (Array.isArray(draft.groupList) ? draft.groupList : []).map((g) => g?.id),
      elementIds: (Array.isArray(draft.elementList) ? draft.elementList : []).map((e) => e?.id),
    });
  } catch {
    return '';
  }
}

export function emptySizeChartRecord(groupList = [], elementList = []) {
  const groupId = String(groupList[0]?.id ?? 1);
  const values = { [groupId]: '' };
  (elementList || []).forEach((el) => {
    values[String(el.id)] = '';
  });
  return { values };
}

/** 提交前去掉空字符串测量值（Tiktok create 遇空值会报 empty size value） */
export function sanitizeSizeChartRecordValues(values = {}, groupList = []) {
  const groupId = String(groupList[0]?.id ?? 1);
  const next = {};
  Object.entries(values && typeof values === 'object' ? values : {}).forEach(([key, raw]) => {
    const k = String(key);
    if (k === 'sizeName' || k === 'groupId' || k === 'name') return;
    const val = raw == null ? '' : String(raw).trim();
    if (!val) return;
    next[k] = val;
  });
  // 尺码名必须保留；若缺失则无法提交
  if (!next[groupId] && values?.[groupId] != null) {
    const sizeName = String(values[groupId]).trim();
    if (sizeName) next[groupId] = sizeName;
  }
  return next;
}

/**
 * 官方样例：meta.elementList 只声明实际有值的测量列。
 * 若把空列（腰围/领围等）也放进 elementList，平台会按声明列校验并拒单。
 * @returns {{ ok: true, elementList: Array, records: Array } | { ok: false, message: string }}
 */
export function pickFilledSizeChartElements({
  records = [],
  groupList = [],
  elementList = [],
} = {}) {
  const groupId = String(groupList[0]?.id ?? 1);
  const sanitizedRecords = (records || []).map((r) => ({
    values: sanitizeSizeChartRecordValues(r?.values || {}, groupList),
  }));
  if (!sanitizedRecords.length) {
    return { ok: false, message: fixedUi('请先在规格供应中选择尺码，再填写尺寸信息') };
  }
  if (sanitizedRecords.some((r) => !String(r.values[groupId] || '').trim())) {
    return { ok: false, message: fixedUi('尺码表存在空的尺码名称，请检查后再提交') };
  }

  const elements = Array.isArray(elementList) ? elementList : [];
  const complete = [];
  for (const el of elements) {
    // 平台测量 ID 是字符串（size_chart_option_bust 等），不能用 Number() 过滤，否则整列被丢
    const id = String(el?.id ?? '').trim();
    if (!id) continue;
    const filledCount = sanitizedRecords.filter((r) => String(r.values[id] || '').trim()).length;
    if (filledCount === 0) continue;
    if (filledCount !== sanitizedRecords.length) {
      return {
        ok: false,
        message: `${getCachedTiktokSizeChartMeasureLabel(el) || formatTiktokSizeChartMeasureLabel(el)}${fixedUi('：请全部尺码填完，或整列留空（不可只填部分尺码）')}`,
      };
    }
    complete.push({
      id: el.id,
      name: String(el.name || el.id || ''),
      ...(el?.necessary != null ? { necessary: Boolean(el.necessary) } : {}),
      ...(el?.role ? { role: String(el.role) } : {}),
    });
  }

  const keepIds = new Set(complete.map((e) => String(e.id)));
  const nextRecords = sanitizedRecords.map((r) => {
    const values = {};
    Object.entries(r.values).forEach(([k, v]) => {
      if (k === groupId || keepIds.has(String(k))) values[k] = v;
    });
    return { values };
  });
  return { ok: true, elementList: complete, records: nextRecords };
}

/** sizecharts.get 单项中的尺码内容 */
export function pickOfficialTemplateContent(template = {}) {
  return template?.contentDTO
    || template?.content
    || template?.mappingContent
    || null;
}

/**
 * 将平台尺码表库（sizecharts.get）套用到当前 meta 表格。
 * 优先按 element id 对齐，其次按名称对齐。
 * @returns {{ records: Array|{null}, name: string, filled: number, businessId: number }}
 */
export function applyOfficialSizeTemplate(template = {}, {
  groupList = [],
  elementList = [],
} = {}) {
  const businessId = tiktokSizeChartId(
    template?.businessId
    || template?.platformBusinessId
    || template?.size_chart_id
    || template?.sizeChartId
    || template?.id,
  );
  const name = String(template?.name || '').trim();
  const content = pickOfficialTemplateContent(template);
  let srcRecords = Array.isArray(content?.records) ? content.records : [];

  // 202608 size_chart_data.table → records（同步入库 / Get 模板常见形态）
  if (!srcRecords.length) {
    const sizeChartData = template?.sizeChartData
      || template?.size_chart_data
      || template?.payload?.sizeChartData
      || template?.payload?.size_chart_data
      || template?.officialSizeChart?.sizeChartData
      || (Array.isArray(template?.sizeChartDatas) ? (
        template.sizeChartDatas[0]?.size_chart_data || template.sizeChartDatas[0]?.sizeChartData
      ) : null)
      || (Array.isArray(template?.payload?.sizeChartDatas) ? (
        template.payload.sizeChartDatas[0]?.size_chart_data
        || template.payload.sizeChartDatas[0]?.sizeChartData
      ) : null)
      || null;
    const converted = recordsFromTiktokSizeChartData(sizeChartData, { groupList, elementList });
    if (converted?.records?.length) {
      return {
        records: converted.records,
        name,
        filled: converted.filled || 0,
        businessId,
      };
    }
    // 站内官方草稿 records（无 table 形态时）
    if (Array.isArray(template?.officialSizeChart?.records) && template.officialSizeChart.records.length) {
      srcRecords = template.officialSizeChart.records;
    } else if (Array.isArray(template?.payload?.officialSizeChart?.records)
      && template.payload.officialSizeChart.records.length) {
      srcRecords = template.payload.officialSizeChart.records;
    }
  }

  if (!srcRecords.length) {
    return { records: null, name, filled: 0, businessId };
  }

  const srcMeta = content?.meta && typeof content.meta === 'object' ? content.meta : {};
  const srcGroups = srcMeta.groupList || srcMeta.groups || [];
  const srcElements = srcMeta.elementList || srcMeta.elements || [];
  const srcGroupId = String(srcGroups[0]?.id ?? groupList[0]?.id ?? 1);
  const dstGroupId = String(groupList[0]?.id ?? 1);

  const srcElByName = new Map();
  (srcElements || []).forEach((el) => {
    const key = String(el?.name || '').trim().toLowerCase();
    if (key && el?.id != null) srcElByName.set(key, String(el.id));
  });

  let filled = 0;
  const records = srcRecords.map((row) => {
    const srcValues = row?.values && typeof row.values === 'object' ? row.values : {};
    const values = { [dstGroupId]: '' };
    const sizeName = srcValues[srcGroupId]
      ?? srcValues[String(srcGroupId)]
      ?? srcValues['1']
      ?? '';
    values[dstGroupId] = String(sizeName || '').trim();
    if (values[dstGroupId]) filled += 1;

    (elementList || []).forEach((el) => {
      const dstId = String(el.id);
      const byId = srcValues[dstId] ?? srcValues[el.id];
      const byNameId = srcElByName.get(String(el?.name || '').trim().toLowerCase());
      const byName = byNameId != null ? (srcValues[byNameId] ?? srcValues[Number(byNameId)]) : undefined;
      const raw = byId != null && String(byId).trim() !== ''
        ? byId
        : (byName != null ? byName : '');
      values[dstId] = raw == null ? '' : String(raw);
      if (String(values[dstId] || '').trim()) filled += 1;
    });
    return { values };
  });

  return { records, name, filled, businessId };
}

/**
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function validateOfficialSizeChartDraft({
  required = false,
  mode = 'skip',
  sizeClass = null,
  selectedBusinessIds = [],
  records = [],
  groupList = [],
  elementList = [],
} = {}) {
  if (!required || mode === 'skip') return { ok: true };
  // 图片模式（TK-PUB-27）：官方允许「平台模板 id 或尺码表图片」二选一，
  // 用户上传尺码表图片且未选模板时，不再强制要求建表。
  if (mode === 'image') return { ok: true };
  const need = Number(sizeClass?.classType) === 1 ? 2 : 1;
  if (mode === 'template') {
    const ids = (selectedBusinessIds || []).map((id) => tiktokSizeChartId(id)).filter(Boolean);
    if (ids.length < need) {
      return {
        ok: false,
        message: need > 1
          ? `${fixedUi('套装类目请选择至少')} ${need} ${fixedUi('个尺码表模板')}`
          : fixedUi('请选择至少一个尺码表模板'),
      };
    }
    return { ok: true };
  }
  if (mode === 'create') {
    const groupId = String(groupList[0]?.id ?? 1);
    const rows = Array.isArray(records) ? records : [];
    if (!rows.length) {
      return { ok: false, message: fixedUi('请先在规格供应中选择尺码，再填写尺寸信息') };
    }
    // meta 未标记 necessary 时，表内全部测量列均视为必填（避免空值提交被 Tiktok 拒绝）
    const elements = Array.isArray(elementList) ? elementList : [];
    const anyNecessary = elements.some((el) => Boolean(el?.necessary));
    const requiredElements = anyNecessary
      ? elements.filter((el) => el?.necessary)
      : elements;
    for (let i = 0; i < rows.length; i += 1) {
      const values = rows[i]?.values || {};
      if (!String(values[groupId] || '').trim()) {
        return { ok: false, message: `${fixedUi('尺码表第')} ${i + 1} ${fixedUi('行请填写尺码名称')}` };
      }
      for (const el of requiredElements) {
        if (!String(values[String(el.id)] || '').trim()) {
          return {
            ok: false,
            message: `${fixedUi('尺码表第')} ${i + 1} ${fixedUi('行请填写')}「${getCachedTiktokSizeChartMeasureLabel(el) || formatTiktokSizeChartMeasureLabel(el)}」`,
          };
        }
      }
    }
    // 可选列：要么整列填满，要么整列留空（平台按 meta.elementList 校验，不允许半填）
    const picked = pickFilledSizeChartElements({ records: rows, groupList, elementList: elements });
    if (!picked.ok) return picked;
    if (!picked.elementList.length) {
      return { ok: false, message: fixedUi('请至少填写一项尺寸测量值') };
    }
    return { ok: true };
  }
  return { ok: false, message: fixedUi('请完善尺码表') };
}

/**
 * TikTok Create Size Chart（/product/202608/size_charts）的 `size_chart_data`：
 * 表头 = 尺码列 + 平台测量列（headers[].id 必须是平台测量 ID，如 size_chart_option_bust），
 * 行 = 每个尺码一行、单元格顺序与 headers 对齐。
 * 与站内尺码库「保存为模板」走同一形状（TiktokStructuredSizeChartPanel.buildSizeChartDataFromDraft）。
 */
export function buildTiktokSizeChartData({
  groupList = [],
  elementList = [],
  records = [],
  measurementType = '',
  unitSystem = 'METRIC',
  isOneSize = false,
  description = '',
} = {}) {
  const groupId = String(groupList[0]?.id ?? 1);
  const headers = [
    { id: SIZE_HEADER_ID, name: String(groupList[0]?.name || 'Size') },
    ...(elementList || []).map((el) => ({
      id: String(el.id),
      name: String(el.name || el.id || ''),
    })),
  ];
  const rows = (records || []).map((row) => {
    const values = row?.values || {};
    const cells = [{ value: String(values[groupId] ?? '').trim() }];
    (elementList || []).forEach((el) => {
      cells.push({ value: String(values[String(el.id)] ?? '').trim() });
    });
    return { cells };
  });
  return {
    table: { headers, rows },
    size_types: ['size_chart_size_type_regular'],
    ...(String(description || '').trim() ? { description: String(description).trim() } : {}),
    is_one_size: Boolean(isOneSize),
    measurement_type: measurementType || 'BODY_MEASUREMENTS',
    unit_system: unitSystem || 'METRIC',
  };
}

/**
 * 提交前解析为发品可用的 sizeTemplateIds。
 */
export async function resolveOfficialSizeTemplateIds({
  shopId,
  catId,
  required = false,
  mode = 'skip',
  sizeClass = null,
  selectedBusinessIds = [],
  chartName = '发品尺码表',
  records = [],
  groupList = [],
  elementList = [],
  measurementType = '',
  api = null,
} = {}) {
  if (!required || mode === 'skip') return [];
  // 图片模式：size_chart.image 直接在载荷里提交，无需创建/引用平台模板
  if (mode === 'image') return [];
  if (!shopId || !api) throw new Error(fixedUi('缺少店铺或尺码表接口'));

  if (mode === 'template') {
    const ids = (selectedBusinessIds || []).map((id) => Number(id)).filter((id) => id > 0);
    if (!ids.length) throw new Error(fixedUi('请选择至少一个尺码表模板'));
    // TikTok 尺码库的模板 id 即 Create Product `size_chart.template.id`，直接使用。
    // 平台没有 Temu 的 tempBusinessId 换发接口，旧实现调用 stub 必然抛「未返回 tempBusinessId」。
    return ids;
  }

  // 发品当场填表：走 PRODUCT_SCOPED（单品尺码），不进可复用模板库，避免「发品尺码表」重名 12052127。
  // 显式「保存为模板」仍走 REUSABLE_TEMPLATE（见 saveOfficialSizeChartAsReusableTemplate）。
  const picked = pickFilledSizeChartElements({ records, groupList, elementList });
  if (!picked.ok) throw new Error(picked.message);
  const sizeChartData = buildTiktokSizeChartData({
    groupList,
    elementList: picked.elementList,
    records: picked.records,
    measurementType,
  });
  let created;
  try {
    created = await api.createPublishSizeChart(shopId, {
      categoryId: String(catId || '').trim(),
      publishToPlatform: true,
      sizeChartMode: 'PRODUCT_SCOPED',
      sizeChartData,
    });
  } catch (e) {
    const raw = String(e?.message || e?.userMessage || e || '').trim();
    if (/empty size value/i.test(raw) || /空的?尺码|尺码.*空/.test(raw)) {
      throw new Error(fixedUi('尺码表存在未填写的尺寸值：带 * 的必填；其它列请整列填完或整列留空后再提交'));
    }
    if (/12052127|duplicate name/i.test(raw)) {
      throw new Error(
        '平台可复用尺码模板名称重复。请改从尺码库选用已有「平台」模板，或上传尺码表图片；填写尺寸发品已改为单品尺码，一般不会再撞名',
      );
    }
    if (/保存尺码数据失败|100021|数据错误|100001/i.test(raw)) {
      throw new Error(
        '平台拒绝保存尺码表：请确认带 * 列已填，且肩宽/袖长等可选列不要只填一部分（空列会自动不传）',
      );
    }
    throw e;
  }
  if (created?.platformWriteSkipped) {
    const detail = String(created.platformError || created.message || '').trim();
    throw new Error(
      detail
        ? `平台尺码表写入失败：${detail}。可改上传尺码表图片，或从已有「平台」来源尺码库选用模板`
        : '平台尺码表写入不可用。可改上传尺码表图片，或从已有「平台」来源尺码库选用模板',
    );
  }
  // 后端返回 { sizeChartId, platformBusinessId }；Create Product 用 size_chart.template.id
  const platformId = String(
    created?.platformBusinessId
    || created?.sizeChartId
    || created?.businessId
    || created?.size_chart_id
    || created?.item?.platformBusinessId
    || '',
  ).trim();
  if (!platformId || platformId === '0') {
    throw new Error(created?.message || fixedUi('平台未返回尺码表 ID，请稍后重试或改从尺码库选用模板'));
  }
  return [platformId];
}

/**
 * 组装官方尺码表 create 入参（content.meta.elementList/records 形状）。
 * @deprecated TikTok `/product/202608/size_charts` 的 `size_chart_data` 是 `table.headers/rows` 形状，
 * 发品建表已改用 `buildTiktokSizeChartData`；此处仅保留给单测与历史调用参考。
 */
export function buildOfficialSizeChartCreatePayload({
  catId,
  sizeClass = null,
  chartName = '发品尺码表',
  records = [],
  groupList = [],
  elementList = [],
  reusable = false,
} = {}) {
  const classId = Number(sizeClass?.classId || 0);
  const classType = Number(sizeClass?.classType || 0);
  if (!(classId > 0)) throw new Error(fixedUi('缺少尺码分类 classId'));
  const groupIdNum = Number(groupList[0]?.id ?? 1) || 1;
  const picked = pickFilledSizeChartElements({ records, groupList, elementList });
  if (!picked.ok) throw new Error(picked.message);
  if (!picked.elementList.length) {
    throw new Error(fixedUi('请至少填写一项尺寸测量值'));
  }
  const payload = {
    classId,
    name: String(chartName || '发品尺码表').trim() || '发品尺码表',
    reusable: Boolean(reusable),
    content: {
      // 官方样例：generalSizeType 与尺码组 id 一致
      generalSizeType: groupIdNum,
      localSizeSource: 1,
      meta: {
        groupList: (groupList || []).map((g) => ({
          id: Number(g.id) || groupIdNum,
          name: String(g.name || fixedUi("尺码")),
        })),
        // 只声明有完整数值的测量列（与官方样例一致）；测量 ID 可能是字符串（size_chart_option_*）
        elementList: picked.elementList.map((e) => ({
          id: e.id,
          name: String(e.name || e.id || ''),
        })),
      },
      records: picked.records,
    },
  };
  if (classType !== 1 && Number(catId) > 0) {
    payload.catId = Number(catId);
  }
  return payload;
}

/**
 * 将当前填写的尺寸保存为平台可复用尺码模板（同步平台尺码表库）。
 * @returns {Promise<{ businessId: string, name: string }>}
 */
export async function saveOfficialSizeChartAsReusableTemplate({
  shopId,
  catId,
  chartName = '发品尺码表',
  records = [],
  groupList = [],
  elementList = [],
  measurementType = '',
  api = null,
} = {}) {
  if (!shopId || !api) throw new Error(fixedUi('缺少店铺或尺码表接口'));
  const check = validateOfficialSizeChartDraft({
    required: true,
    mode: 'create',
    records,
    groupList,
    elementList,
  });
  if (!check.ok) throw new Error(check.message);
  const picked = pickFilledSizeChartElements({ records, groupList, elementList });
  if (!picked.ok) throw new Error(picked.message);
  const name = String(chartName || '发品尺码表').trim() || '发品尺码表';
  const created = await api.createPublishSizeChart(shopId, {
    name,
    categoryId: String(catId || '').trim(),
    publishToPlatform: true,
    sizeChartMode: 'REUSABLE_TEMPLATE',
    sizeChartData: buildTiktokSizeChartData({
      groupList,
      elementList: picked.elementList,
      records: picked.records,
      measurementType,
    }),
  });
  if (created?.platformWriteSkipped) {
    const detail = String(created.platformError || created.message || '').trim();
    return {
      businessId: '',
      name,
      platformWriteSkipped: true,
      message: detail || '平台尺码表写入不可用，已保存到本地尺码库',
      item: created.item || null,
    };
  }
  const platformId = String(
    created?.platformBusinessId
    || created?.sizeChartId
    || created?.businessId
    || created?.item?.platformBusinessId
    || '',
  ).trim();
  if (!platformId || platformId === '0') {
    throw new Error(created?.message || fixedUi('平台未返回尺码表 ID'));
  }
  return { businessId: platformId, name };
}

/** IPE 尺码库是否为官方尺码表语义 */
export function isOfficialSizeChartTemplatePayload(payload = {}) {
  if (!payload || typeof payload !== 'object') return false;
  if (payload.kind === 'tiktok-official' || payload.kind === 'tiktok-platform') return true;
  if (payload.officialSizeChart && typeof payload.officialSizeChart === 'object') return true;
  if (payload.sizeChartData && typeof payload.sizeChartData === 'object') return true;
  return false;
}

/** 组装 IPE 尺码库 payload（官方尺码表语义，可被产品模板 / 发品草稿回填） */
export function buildOfficialSizeChartTemplatePayload({
  categoryId = '',
  productTypeId = '',
  categoryPathLabel = '',
  selectedSizeLabels = [],
  selectedSizeIds = [],
  sizeClass = null,
  officialSizeChart = null,
  platformBusinessIds = [],
} = {}) {
  const labels = (Array.isArray(selectedSizeLabels) ? selectedSizeLabels : [])
    .map((n) => String(n || '').trim())
    .filter(Boolean);
  const businessIds = (Array.isArray(platformBusinessIds) ? platformBusinessIds : [])
    .map((id) => tiktokSizeChartId(id))
    .filter(Boolean);
  return {
    kind: 'tiktok-official',
    categoryId: categoryId ? String(categoryId) : '',
    productTypeId: productTypeId ? String(productTypeId) : '',
    categoryPathLabel: categoryPathLabel ? String(categoryPathLabel) : '',
    selectedSizeLabels: labels,
    selectedSizeIds: (Array.isArray(selectedSizeIds) ? selectedSizeIds : [])
      .map((id) => String(id || '').trim())
      .filter(Boolean),
    sizeClass: sizeClass && typeof sizeClass === 'object' ? sizeClass : null,
    officialSizeChart: officialSizeChart && typeof officialSizeChart === 'object'
      ? officialSizeChart
      : null,
    platformBusinessIds: businessIds,
    // 兼容旧字段：不再写 SHEIN 矩阵 sizeChart
    sizeChart: {},
  };
}

/**
 * 从类目尺码属性 + 已选尺码构造模特试穿尺码选项。
 */
export function buildTiktokModelTryOnSizeOptions({
  sizeAttr = null,
  attrTpl = null,
  selectedSizeIds = [],
  skcList = [],
} = {}) {
  if (!sizeAttr) return [];
  const selected = new Set();
  (Array.isArray(selectedSizeIds) ? selectedSizeIds : []).forEach((id) => {
    const key = String(id || '').trim();
    if (key) selected.add(key);
  });
  (Array.isArray(skcList) ? skcList : []).forEach((skc) => {
    (skc?.skuRows || []).forEach((sku) => {
      const key = String(sku?.sizeValueId || '').trim();
      if (key) selected.add(key);
    });
  });
  if (!selected.size) return [];

  const rawInfos = attrTpl?.raw?.data?.[0]?.attribute_infos
    || attrTpl?.raw?.attribute_infos
    || [];
  const raw = (Array.isArray(rawInfos) ? rawInfos : []).find(
    (r) => String(r?.attribute_id ?? r?.attributeId) === String(sizeAttr.attributeId || ''),
  );
  const rawValues = Array.isArray(raw?.attribute_value_info_list)
    ? raw.attribute_value_info_list
    : [];

  return (sizeAttr.values || [])
    .filter((v) => selected.has(String(v.id)))
    .map((v) => {
      const rawVal = rawValues.find(
        (row) => String(row?.attribute_value_id ?? row?.attributeValueId) === String(v.id),
      );
      const specId = Number(rawVal?.tiktok_spec_id ?? rawVal?.tiktokSpecId ?? v.id) || 0;
      const name = String(v.name || v.nameCn || '').trim();
      if (!name || !(specId > 0)) return null;
      return { id: String(v.id), name, specId };
    })
    .filter(Boolean);
}

/** 模特模板 / 平台同步模特 → goodsModelReqs 单项 */
export function normalizeTiktokTryOnSizeName(name = '') {
  return String(name || '')
    .trim()
    .replace(/码$/u, '')
    .replace(/\s+/g, '')
    .toUpperCase();
}

/** 平台拒「均码 / Free Size」等非商品规格尺码 */
export function isIllegalTiktokTryOnSizeName(name = '') {
  const raw = String(name || '').trim();
  if (!raw) return true;
  const n = normalizeTiktokTryOnSizeName(raw);
  return /^(均码|ONESIZE|ONSIZE|FREESIZE|OS|FREE)$/i.test(n)
    || /^均码$/u.test(raw);
}

/** tryOnResult：TRUE_TO_SIZE(1)/TOO_SMALL(2)/TOO_LARGE(3)，文档可选，默认合身 */
export const TIKTOK_TRY_ON_RESULT_TRUE_TO_SIZE = 1;
export const TIKTOK_TRY_ON_RESULT_OPTIONS = [
  { value: 1, label: '合身' },
  { value: 2, label: '紧身' },
  { value: 3, label: '宽松' },
];

export function normalizeTiktokTryOnResult(value, fallback = TIKTOK_TRY_ON_RESULT_TRUE_TO_SIZE) {
  const n = Number(value);
  if (n === 1 || n === 2 || n === 3) return n;
  return fallback;
}

/**
 * 从商品已选尺码解析试穿尺码（goodsModelReqs.sizeSpecId/Name 必须是商品规格，不是模特库字段）。
 * @param {Array<{ id?: string, name?: string, specId?: number|string }>} sizeOptions
 */
export function resolveGoodsModelTryOnSize(preferredName = '', sizeOptions = []) {
  const options = (Array.isArray(sizeOptions) ? sizeOptions : [])
    .map((opt) => ({
      id: String(opt?.id ?? '').trim(),
      name: String(opt?.name || '').trim(),
      specId: Number(opt?.specId || opt?.id || 0) || 0,
    }))
    .filter((opt) => opt.name && opt.specId > 0);
  if (!options.length) return null;

  const preferredKey = normalizeTiktokTryOnSizeName(preferredName);
  const hit = preferredKey
    ? options.find((opt) => normalizeTiktokTryOnSizeName(opt.name) === preferredKey)
    : null;
  const picked = hit || (options.length === 1 ? options[0] : null);
  if (!picked) return null;
  return {
    sizeSpecId: picked.specId,
    sizeSpecName: picked.name,
    sizeValueId: picked.id,
  };
}

export function mapModelTemplateToGoodsModelReq(item = {}, sizeOptions = []) {
  const payload = item.payload && typeof item.payload === 'object' ? item.payload : {};
  const clothes = payload.clothesModel || item.clothesModel || {};
  const shoe = payload.shoeModel || item.shoeModel || {};
  const modelName = String(
    payload.modelName || item.modelName || item.name || '',
  ).trim();
  const modelProfileUrl = String(
    payload.modelProfileUrl
    || payload.headPortrait
    || payload.avatarUrl
    || payload.thumbUrl
    || item.modelProfileUrl
    || item.headPortrait
    || item.thumbUrl
    || '',
  ).trim();
  const modelId = Number(
    payload.modelId ?? item.modelId ?? item.id ?? 0,
  ) || undefined;
  const preferredSizeName = String(
    payload.sizeSpecName
    || item.sizeSpecName
    || payload.tryOnSize
    || item.tryOnSize
    || '',
  ).trim();
  const resolvedSize = resolveGoodsModelTryOnSize(preferredSizeName, sizeOptions);
  // 禁止默认「均码」：平台会拒「模特试穿尺码不合法：均码」
  const sizeSpecId = resolvedSize?.sizeSpecId
    || Number(payload.sizeSpecId ?? item.sizeSpecId ?? 0)
    || 0;
  const sizeSpecName = resolvedSize?.sizeSpecName
    || (!isIllegalTiktokTryOnSizeName(preferredSizeName) ? preferredSizeName : '');
  const platformModelType = payload.modelType ?? item.modelType;
  // 平台 modelinfo：0 成衣 / 1 鞋模；发品默认成衣=1
  const modelType = platformModelType === 0 || platformModelType === 1
    ? Number(platformModelType) + 1
    : (Number(platformModelType ?? item.modelType ?? 1) || 1);
  const req = {
    modelName: modelName || fixedUi("模特"),
    modelProfileUrl: modelProfileUrl || 'https://img.kwcdn.com/placeholder/model.png',
    modelType,
    modelFeature: Number(payload.modelFeature ?? item.modelFeature ?? 1) || 1,
  };
  if (sizeSpecId > 0) req.sizeSpecId = sizeSpecId;
  if (sizeSpecName) req.sizeSpecName = sizeSpecName;
  if (resolvedSize?.sizeValueId) req.sizeValueId = resolvedSize.sizeValueId;
  if (modelId) req.modelId = modelId;
  const measure = {
    modelHeight: payload.modelHeight ?? item.height ?? clothes.height,
    modelBust: payload.modelBust ?? item.bust ?? clothes.bust,
    modelWaist: payload.modelWaist ?? item.waist ?? clothes.waist,
    modelHip: payload.modelHip ?? item.hip ?? clothes.hipline,
    modelFootLength: payload.modelFootLength ?? item.footLength ?? shoe.footLength,
    modelFootWidth: payload.modelFootWidth ?? item.footWidth ?? shoe.footWidth,
    sizeTemplateId: payload.sizeTemplateId ?? item.sizeTemplateId,
  };
  Object.entries(measure).forEach(([key, val]) => {
    if (val != null && String(val).trim() !== '') req[key] = val;
  });
  req.tryOnResult = normalizeTiktokTryOnResult(payload.tryOnResult ?? item.tryOnResult);
  return req;
}
